import { useApolloClient } from '@apollo/client';
import { t } from '@lingui/macro';
import noop from 'lodash/noop';

import Download from '@decisiv/iconix/lib/components/Download';

import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import { useNotifications } from '@decisiv/ui-components/lib/components/Notifications';

import { USER_EVENT_KINDS, useUserEventHandler } from 'setup/UserEvents';
import { isImportOpen } from 'utils/bulkImportWizardRegistry';

import { stoppedToastCopy, type ToastCopy } from './copy';
import { RESOLVERS } from './resolvers';

const TOAST_DURATION = 10000;

type Notify = ReturnType<typeof useNotifications>['notify'];

function showToast(
  notify: Notify,
  id: string,
  { intent, title, message }: ToastCopy,
  reportUrl?: string,
  { autoDismiss = true }: { autoDismiss?: boolean } = {},
) {
  notify(
    // `id` is not a dedupe key — NotificationsProvider uniquifies it
    // internally — so it's here for traceability. Duplicate delivery is
    // already handled by UserEventsProvider.
    {
      id,
      // Omitting `duration` keeps the toast up until the user closes it.
      ...(autoDismiss && { duration: TOAST_DURATION }),
      intent,
      title,
      // Also what renders the close button, so keep it even though it is a noop.
      onClose: noop,
    } as any,
    <Flex
      flexDirection="column"
      alignItems="flex-start"
      style={{ rowGap: '12px' }}
    >
      {message}
      {reportUrl && (
        <Button
          text={t`Download Report`}
          icon={Download}
          size="small"
          kind="secondary"
          onClick={() => window.location.assign(reportUrl)}
        />
      )}
    </Flex>,
  );
}

/**
 * Surfaces the outcome of a bulk import the user is no longer watching.
 *
 * Renders nothing — it listens app-wide for completion events and raises a
 * toast, so the result still reaches the user after they closed the wizard,
 * navigated away, reloaded, or closed the tab entirely.
 */
export default function BulkImportToasts() {
  const client = useApolloClient();
  const { notify } = useNotifications();

  useUserEventHandler(USER_EVENT_KINDS.BULK_IMPORT_COMPLETED, async (event) => {
    const importId = event.subjectId;
    const importType = (event.payload as { import_type?: string } | null)
      ?.import_type;

    // Nothing identifiable to fetch. Consume it rather than retry forever.
    if (!importId || !importType) return true;

    // The wizard is still open on this import and shows the outcome on its own
    // summary step. Consume it so it can't resurface as a toast later.
    if (isImportOpen(importId)) return true;

    const resolver = RESOLVERS[importType];
    // An import type this build doesn't know about. Leave it unread so a later
    // release can still handle it.
    if (!resolver) return false;

    let outcome;
    try {
      outcome = await resolver.fetch(client, importId);
    } catch {
      // Probably transient — let the next catch-up try again.
      return false;
    }

    if (!outcome) return false;

    showToast(
      notify,
      `bulk-import-${importType}-${importId}`,
      resolver.copy(outcome),
      outcome.reportUrl,
    );

    return true;
  });

  // No isImportOpen check: no wizard shows why it stopped or that rows need
  // manual follow-up, so the toast carries what the summary doesn't.
  useUserEventHandler(USER_EVENT_KINDS.BULK_IMPORT_STOPPED, async (event) => {
    const { subjectId } = event;
    const importType = (event.payload as { import_type?: string } | null)
      ?.import_type;
    const resolver = importType ? RESOLVERS[importType] : undefined;

    let reportUrl: string | undefined;
    if (subjectId && resolver) {
      try {
        // A stopped record is never processing, so null means it is gone.
        reportUrl = (await resolver.fetch(client, subjectId))?.reportUrl;
      } catch {
        // The report button is optional, so the stop still shows without it.
      }
    }

    showToast(
      notify,
      `bulk-import-stopped-${importType ?? 'unknown'}-${subjectId ?? event.id}`,
      stoppedToastCopy(importType),
      reportUrl,
      // A stop needs manual follow-up, so it must not vanish unread.
      { autoDismiss: false },
    );

    return true;
  });

  return null;
}
